import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faFileAlt, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from "../App";

const AddKYC = () => {
  const [propertyId, setPropertyId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({
    id_number: "",
    krapin: "",
    utility_bills: "",
    tenantagreementtemplate: "",
    business_pin: "",
  });
  const [fileNames, setFileNames] = useState({
    id_number: "",
    krapin: "",
    utility_bills: "",
    tenantagreementtemplate: "",
    business_pin: "",
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const storedPropertyId = localStorage.getItem("selectedPropertyId");
    const userDataString = localStorage.getItem("user");
    const userData = userDataString ? JSON.parse(userDataString) : null;
    const storedOwnerId = userData?.unique;

    if (!storedPropertyId || !storedOwnerId) {
      toast.error("Property or Owner not selected.");
      navigate("/select-property");
    } else {
      setPropertyId(storedPropertyId);
      setOwnerId(storedOwnerId);
    }
  }, [navigate]);

  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoading(true);
      const res = await axios.post(
        `${StandardURL}/iNyumba/kyc/kyc/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const fileReference = res.data?.response?.coverUri || "";
      setUploadedDocs((prev) => ({ ...prev, [fieldName]: fileReference }));
      setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
      toast.success(`${fieldName.replace("_", " ")} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${fieldName.replace("_", " ")}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const allDocsUploaded = Object.values(uploadedDocs).every((value) => value && value.trim() !== "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = Object.entries(uploadedDocs)
      .filter(([_, value]) => !value)
      .map(([key]) => key.replace("_", " "));
    if (missing.length > 0) {
      toast.error(`Please upload: ${missing.join(", ")}`);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(
        `${StandardURL}/iNyumba/propertykyc/add/property/${propertyId}/owner/${ownerId}`,
        uploadedDocs,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(res.data.response || "KYC saved successfully");
      navigate("/landlord/home");
    } catch (error) {
      toast.error(error.response?.data?.response || "Failed to save KYC");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
          Upload KYC Documents 📝
        </h1>
        <p className="text-gray-600 text-center mb-8">
          Please upload the required documents to complete your KYC process.
        </p>

        {loading && (
          <div className="flex items-center justify-center mb-6">
            <Oval height={40} width={40} color="#4F46E5" secondaryColor="#818CF8" strokeWidth={4} />
            <span className="ml-3 text-lg text-gray-700">Processing...</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {[
            { key: "id_number", label: "ID Number" },
            { key: "krapin", label: "KRA PIN" },
            { key: "utility_bills", label: "Utility Bills" },
            { key: "tenantagreementtemplate", label: "Tenant Agreement Template" },
            { key: "business_pin", label: "Business PIN" },
          ].map(({ key, label }) => (
            <div
              key={key}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div className="flex-1">
                <label
                  htmlFor={key}
                  className="block text-sm font-medium text-gray-700 mb-1 flex items-center"
                >
                  <FontAwesomeIcon
                    icon={faFileAlt}
                    className="mr-2 text-blue-500"
                    aria-hidden="true"
                  />
                  {label}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id={key}
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileUpload(key, e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                    disabled={loading}
                    aria-describedby={`${key}-status`}
                  />
                  {fileNames[key] && (
                    <div className="mt-2 flex items-center text-sm text-gray-600">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-2 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="truncate">{fileNames[key]}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => document.getElementById(key).click()}
                className="mt-2 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" aria-hidden="true" />
                Upload
              </button>
            </div>
          ))}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={loading || !allDocsUploaded}
              className={`inline-flex items-center px-6 py-3 rounded-lg text-white font-semibold text-lg transition-all duration-300 ease-in-out transform hover:scale-105 ${
                loading || !allDocsUploaded
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              }`}
            >
              {loading ? (
                <>
                  <Oval height={20} width={20} color="#FFF" secondaryColor="#E5E7EB" strokeWidth={4} />
                  <span className="ml-2">Saving...</span>
                </>
              ) : (
                "Save KYC"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddKYC;